import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { DbUser } from "../models";
import { BaseDbObject } from "../models/db";
import { Customer } from "./reports";
import {
    fetchApiDelete,
    fetchApiGet,
    fetchApiPost,
    fetchApiPut,
} from "./utils";

export const OriginalParsedSampleGradeValidator = z.object({
    type: z.string(),
    value: z.string()
});

export const OriginalParsedSampleValidator = z.object({
    id: z.string().trim(),
    location: z.string().trim(),
    date: z.string().trim(),
    commodity: z.string().trim(),
    remarks: z.string().trim().optional(),
    grades: z.array(OriginalParsedSampleGradeValidator)
});

export const OriginalParsedSampleValidators = z.array(OriginalParsedSampleValidator);

export interface GrainSample extends BaseDbObject {
  sample_id: string;
  location: string;
  date: string;
  commodity: string;
  remarks: string;
  grades: Record<string, string>;

  hash: string;
  source_type: string;

  GrainSampleDownloads?: GrainSampleDownload[];
}

export interface GrainSampleBatchDownload extends BaseDbObject {
  user_id: string;
  downloaded_date: Date;
  ticket_count: number;
  is_historical_download: boolean;

  User?: DbUser;
  GrainSampleDownloads?: GrainSampleDownload[];
}

export interface GrainSampleDownload extends BaseDbObject {
  grain_sample_id: string;
  grain_sample_batch_download_id: string;

  GrainSample?: GrainSample;
  GrainSampleBatchDownload?: GrainSampleBatchDownload;
}

export function useListGrainSamplesToDownloadBreakdown() {
    return useQuery({
        queryKey: ["grain", "samples", "summary"],
        queryFn: () => fetchApiGet<{ count: number; summary: Record<string, number> }>(`grain/samples`),
        retry: 1,
    });
}

export function useListGrainSamplesActive() {
    return useQuery({
        queryKey: ["grain", "samples", "list"],
        queryFn: () => fetchApiGet<GrainSample[]>(`grain/samples/list`),
        retry: 1,
    });
}

export function useListGrainSamplesDownloadedBatches() {
    return useQuery({
        queryKey: ["grain", "samples", "batches"],
        queryFn: () => fetchApiGet<GrainSampleBatchDownload[]>(`grain/samples/batches`),
        retry: 1,
    });
}

export function useDownloadLatestSamples() {
    const queryClient = useQueryClient();

    return async () => {
        const queryParams = new URLSearchParams();

        const result = await fetchApiPost<{link: string}>(
            `grain/samples/download`,
            {},
            queryParams
        );

        if (result.success) {
            queryClient.invalidateQueries({ queryKey: ["grain", "samples"] });
        }

        return result;
    };
}

export function useAttachCustomerTo() {
    const queryClient = useQueryClient();

    return async (data: { id: string; customer_id: string}) => {
        const queryParams = new URLSearchParams();

        const result = await fetchApiPut<{link: string}>(
            `grain/samples/${data.id}/client`,
            data,
            queryParams
        );

        if (result.success) {
            queryClient.invalidateQueries({ queryKey: ["grain", "samples"] });
        }

        return result;
    };
}

export function useDownloadSampleBatch() {
    const queryClient = useQueryClient();

    return async (batchId: string) => {
        const queryParams = new URLSearchParams();

        const result = await fetchApiPost<{link: string}>(
            `grain/samples/batches/${batchId}/download`,
            { id: batchId },
            queryParams
        );

        if (result.success) {
            queryClient.invalidateQueries({ queryKey: ["grain", "samples"] });
        }

        return result;
    };
}
